/**
 * Core slugify for camp URL segments (location names, typed slugs, org slugs read from DB).
 * - Unicode letters are ASCII-folded (e.g. ü → u, ç → c).
 * - `@` becomes the word `at` (so "YMCA @ RTP" → ymca-at-rtp).
 * - `&` becomes the word `and` (so "Tennis & Paddle" → tennis-and-paddle).
 * - `North Carolina` (whole phrase) → `nc` (so "Camp North Carolina" → camp-nc).
 * - `St.` / `Saint` (word start) → `st` (so "St. Mark's …" → st-marks-…).
 * - Apostrophes (ASCII and curly) are removed (so "O'Brien's …" → obriens-…).
 * - Runs of single-letter segments (e.g. "A.E." → a-e-…) merge into one token (e.g. "ae-…").
 * - Underscores and other non-alphanumeric runs become hyphens (composite ids use `_` only between org and location segments).
 */
function mergeAdjacentSingleLetterSlugParts(s: string): string {
  const parts = s.split("-").filter((p) => p.length > 0);
  if (parts.length === 0) return "";
  const out: string[] = [];
  let buf = "";
  const flushBuf = () => {
    if (buf) {
      out.push(buf);
      buf = "";
    }
  };
  for (const p of parts) {
    if (/^[a-z]$/.test(p)) {
      buf += p;
    } else {
      flushBuf();
      out.push(p);
    }
  }
  flushBuf();
  return out.join("-");
}

export function slugifyToCampUrlSegment(raw: string): string {
  let s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";

  s = s.normalize("NFKD").replace(/\p{M}/gu, "");
  s = s.replace(/ß/g, "ss");

  s = s.replace(/@/g, " at ");
  s = s.replace(/&/g, " and ");

  s = s.replace(/\bnorth carolina\b/g, " nc ");

  s = s.replace(/\b(saint|st)\.?\s+/gi, "st ");

  s = s.replace(/['\u2018\u2019]/g, "");

  s = s.replace(/[^a-z0-9]+/g, "-");
  s = s.replace(/-+/g, "-").replace(/^-|-$/g, "");

  return mergeAdjacentSingleLetterSlugParts(s);
}

/**
 * Normalizes a single URL segment for camp org/location public routes.
 * Result is lowercase [a-z0-9-]+ suitable for use in `campPublicRoutes` doc ids
 * joined as `${orgSlug}_${locationSlug}`.
 */
export function normalizeCampSlugSegment(raw: string): string {
  return slugifyToCampUrlSegment(raw);
}

export function isValidCampSlugSegment(normalized: string): boolean {
  if (!normalized) return true;
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(normalized) && normalized.length <= 80;
}

export function buildCampPublicRouteDocId(orgSlug: string, locationSlug: string): string {
  return `${normalizeCampSlugSegment(orgSlug)}_${normalizeCampSlugSegment(locationSlug)}`;
}
